import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FirmCollaboratorActions } from '@app/core/state/firm-collaborators/firm-collaborators.actions';
import { FirmCollaboratorSelectors } from '@app/core/state/firm-collaborators/firm-collaborators.selectors';
import { FirmCollaboratorType } from '@app/core/state/types/firm-collaborator.types';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { DialogResult } from '@app/shared/components/form-dialog-base/form-dialog-base.component';
import { TableColumn } from '@app/shared/components/table/interfaces/table-column';
import { BystTableModule } from '@app/shared/components/table/table.module';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';
import { AddFirmCollaboratorDialogComponent } from './add-firm-collaborator-dialog.component';
import { ConfirmRemoveFirmCollaboratorDialogComponent } from './confirm-remove-firm-collaborator-dialog.component';

interface FirmCollaboratorDialogData {
  clientId: number;
}

const TRANSLATION_SCOPE = 'firmCollaboratorDialogComponent';

@Component({
  template: `<app-table
      [columns]="columns$ | async"
      [data]="tableData$ | async"
      [columnSelector]="false"
      [searchField]="false"
      [paginator]="false">
      <ng-template tableTemplate="bodyCell" tableBodyCellField="action" let-rowData="rowData">
        <div class="text-center">
          <span (click)="removeFirmCollaborator(rowData)" class="far fa-trash-alt cursor-pointer"></span>
        </div>
      </ng-template>
    </app-table>
    <div class="flex justify-between mt-4" *transloco="let t; prefix: scope">
      <bl-frontend-button
        [text]="t('addFirmCollaborator')"
        variant="secondary"
        (onclick)="showAddFirmCollaboratorsDialog()"></bl-frontend-button>
      <bl-frontend-button [text]="'shared.close' | transloco" (onclick)="close(null)"></bl-frontend-button>
    </div>`,
  standalone: true,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [CommonModule, BystTableModule, BlFrontendButtonComponent, TranslocoModule],
})
export class FirmCollaboratorDialogComponent extends TranslatableFormDialogBaseComponent<
  FirmCollaboratorDialogData,
  DialogResult
> {
  tableData$: Observable<FirmCollaboratorType[]>;
  columns$: Observable<TableColumn[]>;

  private columns: TableColumn[] = [
    { field: 'name', header: 'connectedBlFirm' },
    { field: 'firmKey', header: 'firmKey' },
    { field: 'action', header: 'remove', width: '80px', sortable: false },
  ];

  private currentClientId: number;

  protected override onInitialized = () => {
    this.currentClientId = this.config.data.clientId;

    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns));

    this.tableData$ = this.store.select(
      FirmCollaboratorSelectors.selectFirmCollaboratorsByClientId(this.currentClientId),
    );
  };

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): DynamicDialogConfig<FirmCollaboratorDialogData> {
    return {
      header: translation.firmCollaborators,
      width: '700px',
    };
  }

  removeFirmCollaborator(firmCollaborator: FirmCollaboratorType) {
    openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: ConfirmRemoveFirmCollaboratorDialogComponent,
      data: {
        firmCollaborator,
      },
    }).subscribe((result) => {
      if (result?.confirmed) {
        this.store.dispatch(
          FirmCollaboratorActions.removeFirmCollaborator({
            clientId: this.currentClientId,
            firmKey: firmCollaborator.firmKey,
          }),
        );
      }
    });
  }

  showAddFirmCollaboratorsDialog() {
    openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: AddFirmCollaboratorDialogComponent,
      data: { clientId: this.currentClientId },
    });
  }
}
